import { ApplicationType } from '@squareup/dex-types-shared-application-type';
import { SandboxToggle } from '@squareup/dex-ui-dex-tech-main-sandbox-toggle';
import { Box } from '@squareup/dex-ui-shared-base';
import { MarketActivityIndicator } from '@squareup/dex-ui-shared-market';
import { publishUnhandledError } from '@squareup/dex-utils-application-behavior-events';
import {
  getNextPublicBaseURL,
  getNextPublicGraphqlExplorerCDNURL,
  getNextPublicGraphqlSandboxUrl,
  getNextPublicGraphqlUrl,
  getNextPublicSquareURL,
} from '@squareup/dex-utils-environment';
import { ScriptLoader } from '@squareup/dex-utils-scripts';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import styles from './graphql-explorer-page.module.css';

declare global {
  interface Window {
    changeGraphqlEnvironment: (newEnv: string) => void;
    renderGraphqlExplorer: (
      div: Element,
      developerUrl: string,
      initialEnv: string,
      graphqlUrl: string,
      graphqlSandboxUrl: string,
      signInUrl: string,
      userDataUrl: string,
      useSplitUsersEndpointFlag?: boolean
    ) => void;
  }
}

const GraphqlExplorerPage = () => {
  const [scriptLoaded, setScriptIsLoaded] = useState(false);
  const divRef = useRef<HTMLDivElement>(null);

  const [environment, setEnvironment] = useState(ApplicationType.Sandbox);

  const fetchManifest = async () => {
    try {
      const response = await fetch(
        `${getNextPublicGraphqlExplorerCDNURL()}/asset-manifest.json`
      );

      if (response.ok) {
        return response.json();
      } else {
        throw new Error(
          `Error when fetching GraphQL explorer manifest: ${response.status} ${response.statusText}`
        );
      }
    } catch (error) {
      publishUnhandledError(error as Error);
      return null;
    }
  };

  const fetchAssets = useCallback(async () => {
    let manifest;

    try {
      manifest = await fetchManifest();
    } catch (error) {
      publishUnhandledError(error as Error);
    }

    if (!manifest) {
      return;
    }

    // Inject the script
    const scriptSrc = `${getNextPublicGraphqlExplorerCDNURL()}${
      manifest.files['main.js']
    }`;

    const scriptLoader = new ScriptLoader();
    await scriptLoader.load(window, (script: HTMLScriptElement) => {
      script.setAttribute('src', scriptSrc);
    });

    setScriptIsLoaded(true);

    // Inject the styles
    const stylesLocation = `${getNextPublicGraphqlExplorerCDNURL()}${
      manifest.files['main.css']
    }`;

    const linkElement = document.createElement('link');
    linkElement.rel = 'stylesheet';
    linkElement.type = 'text/css';
    linkElement.href = stylesLocation;
    linkElement.media = 'all';

    document.head.appendChild(linkElement);
  }, []);

  useEffect(() => {
    async function initializeGraphqlExplorer() {
      await fetchAssets();
    }

    initializeGraphqlExplorer();
  }, [fetchAssets]);

  useEffect(() => {
    const developerUrl = getNextPublicBaseURL();
    const graphqlUrl = getNextPublicGraphqlUrl();
    const graphqlSandboxUrl = getNextPublicGraphqlSandboxUrl();
    const signInUrl = `${getNextPublicSquareURL()}/login?app=developer&return_to=${getNextPublicBaseURL()}/explorer/graphql`;
    const userDataUrl = `${getNextPublicBaseURL()}/api/developer-data/me`;

    // This always evaluates to true now in LaunchDarkly. We should eventually update GraphQL Explorer to no longer need it.
    const useSplitUsersEndpointFlag = true;

    if (scriptLoaded && divRef.current) {
      window.renderGraphqlExplorer(
        divRef.current,
        developerUrl,
        environment.toLowerCase(),
        graphqlUrl,
        graphqlSandboxUrl,
        signInUrl,
        userDataUrl,
        useSplitUsersEndpointFlag
      );
    }

    // I don't want this to re-run if the environment changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptLoaded]);

  return (
    <Box padding={{ top: '1x' }}>
      {scriptLoaded ? (
        <>
          <Box padding={{ bottom: '4x' }}>
            <SandboxToggle
              onChange={(e) => {
                setEnvironment(e);
                window.changeGraphqlEnvironment?.(e.toLowerCase());
              }}
              value={environment}
            />
          </Box>
          <div ref={divRef} className={styles['graphql-explorer-body']} />
        </>
      ) : (
        <Box
          padding={{ vertical: '2x' }}
          className={styles['loading-container']}
        >
          <MarketActivityIndicator size="small" />
        </Box>
      )}
      <Box border={{ line: { bottom: 'standard' } }}></Box>
    </Box>
  );
};

export { GraphqlExplorerPage };
